import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Filter, Calendar, User, Truck, Tag, Search, RotateCcw, X, Loader2 } from 'lucide-react';
import { getAgregadosMotoristasList, isPessoaAtiva } from '../../services/pessoaService';

export interface OsFilterState {
  data_atendimento: string;
  data_atendimento_fim: string;
  pedido_id: string;
  agregado: string;
  status_id: string;
  id: string;
}

interface OsFiltrosPanelProps {
  filters: OsFilterState;
  setFilters: React.Dispatch<React.SetStateAction<OsFilterState>>;
  onSearch: () => void;
  onClear: () => void;
  onClose?: () => void;
}

export const OsFiltrosPanel: React.FC<OsFiltrosPanelProps> = ({
  filters,
  setFilters,
  onSearch,
  onClear,
  onClose,
}) => {
  const [pedidosOptions, setPedidosOptions] = useState<{ id: number; nome_cliente: string }[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ id: number; status: string; cor: string }[]>([]);
  const [agregadosOptions, setAgregadosOptions] = useState<{ id: number; nome: string }[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    setLoadingOptions(true);
    try {
      // 1. Clientes ativos através da tabela Pedidos + Pessoa
      const { data: pedidosData } = await supabase
        .from('pedido')
        .select('id, pessoa_id')
        .order('id', { ascending: false });

      if (pedidosData && pedidosData.length > 0) {
        const pessoaIds = Array.from(new Set(pedidosData.map((p) => p.pessoa_id).filter(Boolean)));
        
        let pessoaMap = new Map<number, { nome: string; ativo: boolean }>();
        const chunkSize = 200;
        for (let i = 0; i < pessoaIds.length; i += chunkSize) {
          const chunk = pessoaIds.slice(i, i + chunkSize);
          const { data: pessoasData } = await supabase
            .from('pessoa')
            .select('id, nome, ativo, data_desativacao, deleted_at, situacao_cadastral')
            .in('id', chunk);
          (pessoasData || []).forEach((p) => {
            const isAtivo = isPessoaAtiva(p);
            pessoaMap.set(p.id, { nome: p.nome, ativo: isAtivo });
          });
        }

        // Filtra apenas pedidos cujos clientes são ativos (ou pedidos selecionados atualmente)
        const activePedidos = pedidosData.filter((p) => {
          const pInfo = pessoaMap.get(p.pessoa_id);
          const isSelected = String(filters.pedido_id) === String(p.id);
          return (pInfo && pInfo.ativo) || isSelected;
        });

        setPedidosOptions(
          activePedidos.map((p) => ({
            id: p.id,
            nome_cliente: pessoaMap.get(p.pessoa_id)?.nome || `Pedido #${p.id}`,
          }))
        );
      }

      // 2. Status
      const { data: statusData } = await supabase
        .from('status')
        .select('id, status, cor')
        .order('id');
      if (statusData) setStatusOptions(statusData);

      // 3. Agregados / Motoristas Agregados (sem fornecedores, obras ou bancos)
      const agregadosList = await getAgregadosMotoristasList();
      setAgregadosOptions(agregadosList);
    } catch (err) {
      console.error('Erro ao carregar opções de filtro:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-sm space-y-4 mb-4">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-red-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
            Filtros Avançados de Pesquisa
          </h3>
          {loadingOptions && (
            <span className="flex items-center gap-1 text-2xs text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin text-red-600" />
              Carregando opções...
            </span>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
            title="Fechar filtros"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Intervalo de Datas */}
        <div className="sm:col-span-2 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-2xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3 text-red-600" />
              Data Atendimento De
            </label>
            <input
              type="date"
              name="data_atendimento"
              value={filters.data_atendimento}
              onChange={handleChange}
              className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white"
            />
          </div>

          <div>
            <label className="block text-2xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3 text-red-600" />
              Até
            </label>
            <input
              type="date"
              name="data_atendimento_fim"
              value={filters.data_atendimento_fim}
              onChange={handleChange}
              className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white"
            />
          </div>
        </div>

        {/* Cliente / Pedido */}
        <div>
          <label className="block text-2xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
            <User className="w-3 h-3 text-red-600" />
            Cliente / Pedido
          </label>
          <select
            name="pedido_id"
            value={filters.pedido_id}
            onChange={handleChange}
            className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white"
          >
            <option value="">Todos os clientes ({pedidosOptions.length})...</option>
            {pedidosOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome_cliente} (Pedido #{p.id})
              </option>
            ))}
          </select>
        </div>

        {/* Agregado */}
        <div>
          <label className="block text-2xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
            <Truck className="w-3 h-3 text-red-600" />
            Agregado ({agregadosOptions.length})
          </label>
          <select
            name="agregado"
            value={filters.agregado}
            onChange={handleChange}
            className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white font-medium"
          >
            <option value="">Todos os agregados ({agregadosOptions.length})...</option>
            {agregadosOptions.map((a) => (
              <option key={a.id} value={a.id}>
                {a.nome}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div>
          <label className="block text-2xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
            <Tag className="w-3 h-3 text-red-600" />
            Status
          </label>
          <select
            name="status_id"
            value={filters.status_id}
            onChange={handleChange}
            className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white"
          >
            <option value="">Todos os status...</option>
            {statusOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.status}
              </option>
            ))}
          </select>
        </div>

        {/* Número da O.S (Pesquisa Múltipla) */}
        <div className="sm:col-span-3">
          <label className="block text-2xs font-bold text-slate-600 uppercase mb-1 flex items-center gap-1">
            <Search className="w-3 h-3 text-red-600" />
            Nº O.S (Ex: 101, 102, 103)
          </label>
          <input
            type="text"
            name="id"
            value={filters.id}
            onChange={handleChange}
            placeholder="Digite o número da OS ou vários separados por vírgula..."
            className="w-full border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:ring-2 focus:ring-red-500 outline-none bg-white"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
        <button
          type="button"
          onClick={onClear}
          className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-xs font-bold transition-colors flex items-center gap-1 cursor-pointer"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Limpar Filtros
        </button>

        <button
          type="button"
          onClick={onSearch}
          className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1 shadow-xs cursor-pointer"
        >
          <Search className="w-3.5 h-3.5" />
          Aplicar Filtros
        </button>
      </div>
    </div>
  );
};
